import React, { FC, useMemo, useRef, useState } from 'react';
import styles from '../journey-explorer.module.scss';
import { useJourneyApi } from '../journey-api-context';
import { CustomerTimeline as Timeline } from '../../../../models/journey-explorer/journey-explorer';
import { formatDate, formatDateTime, formatNumber, formatSpan } from '../journey-explorer-utils';
import { InteractionInfo, resolveInfo } from '../journey-map-layout';
import { RequestLoader, useDebouncedRequest } from '../use-debounced-request';

const LOAD_ERROR = 'This customer could not be loaded.';

interface CustomerTimelineProps {
	customerId: string;
	lookup: Map<string, InteractionInfo>;
}

const CustomerTimeline: FC<CustomerTimelineProps> = ({ customerId, lookup }) => {
	const apiService = useJourneyApi();
	const apiRef = useRef(apiService);
	apiRef.current = apiService;
	const [attempt, setAttempt] = useState(0);

	const load = useMemo<RequestLoader<Timeline>>(
		() => (signal) => apiRef.current.getTimeline(customerId, signal),
		[customerId]
	);
	const { data, error } = useDebouncedRequest(load, LOAD_ERROR, attempt);

	if (error) {
		return (
			<div className={styles.drawer_message}>
				{error}
				<button type="button" className={styles.retry} onClick={() => setAttempt((value) => value + 1)}>
					Retry
				</button>
			</div>
		);
	}
	if (!data || data.customer_id !== customerId) {
		return (
			<div className={styles.drawer_message}>
				<span className={styles.spinner} aria-label="Loading" />
			</div>
		);
	}

	const details = [
		data.start_date && `Started ${formatDate(data.start_date)}`,
		...Object.values(data.attributes).filter(Boolean),
	].filter(Boolean);

	return (
		<div>
			<div className={styles.timeline_header}>
				<span className={styles.timeline_details}>{details.join(' · ')}</span>
			</div>
			{data.steps.length === 0 && <div className={styles.drawer_message}>No interactions recorded.</div>}
			<ol className={styles.timeline}>
				{data.steps.map((step, index) => {
					const info = resolveInfo(lookup, step.name, step.type ?? undefined);
					const previous = index > 0 ? resolveInfo(lookup, data.steps[index - 1].name).label : null;
					const when = [
						step.timestamp && formatDateTime(step.timestamp),
						previous &&
							step.secs_since_previous !== null &&
							`${formatSpan(step.secs_since_previous)} after ${previous}`,
					].filter(Boolean);
					return (
						<li key={`${index}:${step.name}`} className={styles.timeline_step}>
							<span className={styles.timeline_dot} style={{ background: info.color }} />
							<span className={styles.timeline_name}>{info.label}</span>
							<span className={styles.timeline_when}>{when.join(' · ')}</span>
						</li>
					);
				})}
			</ol>
			{data.truncated && (
				<div className={styles.drawer_message}>
					Showing the first {formatNumber(data.steps.length)} interactions.
				</div>
			)}
		</div>
	);
};

export default CustomerTimeline;
